import React from "react";
import { useParams, Navigate } from "react-router-dom";
import DetailPage from "./DetailPage";
import Seo from "./Seo";
import { ACT_DETAILS, CONCEPT_DETAILS, EVENT_PAGES } from "../data/pages";

const BASE = "https://lumiereevents.org";

const serviceLd = ({ name, description, image, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  image: image && image.startsWith("http") ? image : BASE + image,
  provider: { "@id": BASE + "/#org" },
  areaServed: [
    "Greece", "Italy", "France", "Spain", "United Arab Emirates", "Saudi Arabia", "Worldwide",
  ],
  url: BASE + path,
});

/* ---------------- Signature Act ---------------- */
export function ActPage() {
  const { slug } = useParams();
  const act = ACT_DETAILS.find((a) => a.slug === slug);
  if (!act) return <Navigate to="/" replace />;
  const path = `/acts/${slug}`;
  return (
    <>
      <Seo
        path={path}
        title={`${act.headline} — Luxury ${act.headline} Performance | Lumière Entertainment Athens`}
        description={`${act.intro} Available in Greece, Italy, France, Spain, UAE, Saudi Arabia and destination worldwide.`}
        image={act.image}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: act.headline, path },
        ]}
        jsonLd={serviceLd({
          name: `${act.headline} — Signature Act`,
          description: act.intro,
          image: act.image,
          path,
        })}
      />
      <DetailPage
        kicker="Signature Act"
        name={act.headline}
        intro={act.intro}
        longIntro={act.longIntro}
        image={act.image}
        gallery={act.gallery}
        meta={[
          { label: "Duration", value: act.duration },
          { label: "Cast Size", value: act.cast },
        ]}
      />
    </>
  );
}

/* ---------------- Concept ---------------- */
export function ConceptPage() {
  const { slug } = useParams();
  const c = CONCEPT_DETAILS.find((x) => x.slug === slug);
  if (!c) return <Navigate to="/" replace />;
  const path = `/concepts/${slug}`;
  return (
    <>
      <Seo
        path={path}
        title={`${c.name} — Themed Event Concept | Lumière Entertainment Athens`}
        description={`${c.name}: a bespoke themed event concept — ${c.tags.join(", ")}. Full production in Greece, Italy, France, Spain, UAE and destination worldwide.`}
        image={c.image}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Experiences", path: "/experiences" },
          { name: c.name, path },
        ]}
        jsonLd={serviceLd({
          name: `${c.name} — Themed Event Concept`,
          description: c.intro,
          image: c.image,
          path,
        })}
      />
      <DetailPage
        kicker="Event Concept"
        name={c.name}
        intro={c.intro}
        longIntro={c.longIntro}
        image={c.image}
        video={c.video}
        gallery={c.gallery}
        tags={c.tags}
      />
    </>
  );
}

/* ---------------- Event Type ---------------- */
export function EventTypePage() {
  const { slug } = useParams();
  const p = EVENT_PAGES.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/" replace />;
  const path = `/events/${slug}`;
  return (
    <>
      <Seo
        path={path}
        title={`${p.name} — Luxury Event Production | Lumière Entertainment Athens`}
        description={p.intro}
        image={p.heroImage}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Who we work with", path: "/#clients" },
          { name: p.name, path },
        ]}
        jsonLd={serviceLd({
          name: `${p.name} — Bespoke Entertainment`,
          description: p.intro,
          image: p.heroImage,
          path,
        })}
      />
      <DetailPage
        kicker={p.kicker}
        name={p.name}
        intro={p.intro}
        image={p.heroImage}
        gallery={p.gallery}
      />
    </>
  );
}
